import type { ICampaignWorkspaceData } from '../../../redux/buzzbridge/campaignWorkspace.types';
import { formatSocialHandle } from '../../../utils/socialHandle';
import type { IParticipantProfileHeaderProps } from './ParticipantProfileHeader';
import type { IDeliverableRow } from './ParticipantDeliverables';
import { DELIVERABLE_TYPE_LABELS } from '../../../utils/deliverableLabels';

function formatFollowers(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n ?? 0);
}

function followerCount(inf: ICampaignWorkspaceData['creator']['influencer']): number | null {
    if (!inf) return null;
    const nums = [inf.instagram, inf.tiktok, inf.youtube, inf.facebook, inf.twitter]
        .map(p => p?.followers)
        .filter((x): x is number => typeof x === 'number' && x > 0);
    const maxPlat = nums.length ? Math.max(...nums) : 0;
    const reach = typeof inf.totalReach === 'number' && inf.totalReach > 0 ? inf.totalReach : 0;
    const m = Math.max(maxPlat, reach);
    return m > 0 ? m : null;
}

function primaryHandle(
    inf: ICampaignWorkspaceData['creator']['influencer'],
    user: ICampaignWorkspaceData['creator']['user'],
): string {
    if (inf) {
        const platforms = [inf.instagram, inf.tiktok, inf.youtube, inf.twitter, inf.facebook];
        for (const p of platforms) {
            if (p?.username) return formatSocialHandle(p.username);
        }
    }
    if (user && 'userName' in user && user.userName) return formatSocialHandle(user.userName);
    return '—';
}

function platformLabel(inf: ICampaignWorkspaceData['creator']['influencer']): string {
    if (!inf) return '—';
    if (inf.instagram?.username) return 'Instagram';
    if (inf.tiktok?.username) return 'TikTok';
    if (inf.youtube?.username) return 'YouTube';
    return 'Multi';
}

function latestSubmissionStatusLabel(
    submissions: ICampaignWorkspaceData['participant']['content_submissions'],
): string | null {
    if (!submissions?.length) return null;
    const last = submissions[submissions.length - 1];
    const map: Record<string, string> = {
        draft: 'Draft',
        submitted: 'In Review',
        needs_revision: 'Revision Requested',
        approved: 'Approved',
        shared: 'Delivered',
    };
    return map[last.status] ?? last.status;
}

export function mapWorkspaceToProfileHeader(ws: ICampaignWorkspaceData): IParticipantProfileHeaderProps {
    const inf = ws.creator.influencer;
    const user = ws.creator.user;
    const name =
        inf?.name?.trim() ||
        (user && 'firstName' in user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '') ||
        'Creator';
    const fc = followerCount(inf);
    const tags =
        inf?.categories?.map(c =>
            typeof c === 'object' && c && 'main' in c ? (c.sub ? `${c.main} · ${c.sub}` : c.main) : String(c),
        ) ?? [];

    const createdBy = inf?.created_by;
    const userName =
        (typeof createdBy === 'object' && createdBy !== null ? (createdBy as any).userName : undefined) ||
        (user && 'userName' in user ? (user as any).userName : undefined) ||
        inf?.instagram?.username ||
        inf?.tiktok?.username ||
        inf?.youtube?.username;

    return {
        name,
        handle: primaryHandle(inf, user),
        avatar: inf?.profile_picture,
        profileUrl: userName ? `/creator/${userName}` : undefined,
        isPending: ws.participant.status === 'pending',
        contentStatus: latestSubmissionStatusLabel(ws.participant.content_submissions),
        tags,
        followersLabel: fc != null ? formatFollowers(fc) : '—',
        engagementLabel: typeof inf?.avg_engagement_rate === 'number' ? `${inf.avg_engagement_rate}%` : '—',
        platform: platformLabel(inf),
    };
}

function submissionStatusToRow(status: string | undefined): 'pending' | 'submitted' | 'approved' | 'revision' {
    switch (status) {
        case 'approved':
        case 'shared':
            return 'approved';
        case 'submitted':
            return 'submitted';
        case 'needs_revision':
            return 'revision';
        default:
            return 'pending';
    }
}

export function buildDeliverableRows(ws: ICampaignWorkspaceData): IDeliverableRow[] {
    const deliverables = ws.campaign.deliverables ?? [];
    const labels: string[] = deliverables.length
        ? deliverables.map(d => DELIVERABLE_TYPE_LABELS[d.type] ?? d.type)
        : ['Deliverables'];
    const subs = ws.participant.content_submissions ?? [];
    return labels.map((label, i) => ({
        label,
        status: submissionStatusToRow(subs[i]?.status),
    }));
}
